import * as React from 'react'
import { styled, css } from 'next-yak'

import * as breakpoints from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as effects from '../constants/effects'
import { withPrivatePublicMap } from '@owl-nest/yak/extra-runtime'

type AvatarSizes = 'extra-small' | 'small' | 'medium' | 'large' | 'extra-large'

const styles = {
  extraSmall: css`
    height: 30px;
    width: 30px;
  `,
  small: css`
    height: 36px;
    width: 36px;
  `,
  medium: css`
    height: 50px;
    width: 50px;
  `,
  large: css`
    height: 90px;
    width: 90px;
  `,
  extraLarge: css`
    height: 180px;
    width: 180px;
  `,
}

type VariantProps = {
  $size?: AvatarSizes
}

const dynamicAvatar = css<VariantProps>`
  ${({ $size = 'small' }) => {
    // can't use `styles[$size]` because yak can't parse member
    // expression and infer what styles are possible. We need to explicitly
    // return each possible style with nested ifs.
    if ($size === 'extra-small') {
      return styles.extraSmall
    }
    if ($size === 'small') {
      return styles.small
    }
    if ($size === 'medium') {
      return styles.medium
    }
    if ($size === 'large') {
      return styles.large
    }
    if ($size === 'extra-large') {
      return styles.extraLarge
    }
  }}
`

type ResponsiveProps = {
  $asDesktop?: AvatarSizes
}

const responsiveAvatar = css<ResponsiveProps>`
  @media screen and ${breakpoints.TABLET} {
    ${({ $asDesktop }) => {
      // can't use `styles[$size]` because yak can't parse member
      // expression and infer what styles are possible. We need to explicitly
      // return each possible style with nested ifs.
      if ($asDesktop === 'extra-small') {
        return styles.extraSmall
      }
      if ($asDesktop === 'small') {
        return styles.small
      }
      if ($asDesktop === 'medium') {
        return styles.medium
      }
      if ($asDesktop === 'large') {
        return styles.large
      }
      if ($asDesktop === 'extra-large') {
        return styles.extraLarge
      }
      return ''
    }}
  }
`

type PrivateAvatarProps = { $withShadow?: boolean } & VariantProps & ResponsiveProps

const BaseAvatar = styled.img<PrivateAvatarProps>`
  ${({ src }) =>
    src &&
    css`
      background-image: ${`url(${src})`};
    `}
  background-position: center;
  background-size: cover;
  border-radius: 50%;
  border: 2px solid ${colors.PRIMARY_GREY_000};
  object-fit: cover;
  box-sizing: border-box;

  ${dynamicAvatar}

  ${({ $withShadow = false }) => {
    if ($withShadow) {
      return css`
        box-shadow: ${effects.SHADOW_2};
      `
    }
  }};

  ${responsiveAvatar}
`
export type AvatarProps = {
  /** @default 'small' */
  type?: AvatarSizes
  withShadow?: boolean
  asDesktop?: AvatarSizes
} & React.JSX.IntrinsicElements['img']

export const Avatar = styled(
  withPrivatePublicMap(BaseAvatar, ({ withShadow, type, asDesktop, ...props }: AvatarProps) => ({
    ...props,
    $withShadow: withShadow,
    $size: type,
    $asDesktop: asDesktop,
  })),
)``

Avatar.displayName = 'image.Avatar'

type PastilleProps = {
  /** @default 'medium' */
  size?: 'extraSmall' | 'small' | 'medium'
  /** @default false */
  noBorder?: boolean
}

type PrivatePastillProps = { [K in keyof PastilleProps as `$${K}`]: PastilleProps[K] }

const BasePastille = styled.div<PrivatePastillProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  * {
    max-width: 120px;
    max-height: 120px;
    text-align: center;
  }

  ${({ $size = 'medium' }) => {
    if ($size === 'extraSmall') {
      return css`
        height: 40px;
        width: 40px;

        ${({ $noBorder }) =>
          !$noBorder &&
          css`
            box-shadow: 0 0 0 2px ${colors.PRIMARY_GREY_000};
          `}
      `
    }
    if ($size === 'small') {
      return css`
        height: 60px;
        width: 60px;

        ${({ $noBorder }) =>
          !$noBorder &&
          css`
            box-shadow: 0 0 0 2px ${colors.PRIMARY_GREY_000};
          `}
      `
    }
    if ($size === 'medium') {
      return css`
        width: 120px;
        height: 120px;

        ${({ $noBorder }) =>
          !$noBorder &&
          css`
            box-shadow: 0 0 0 5px ${colors.PRIMARY_GREY_000};
          `}
      `
    }
  }}
`

export const Pastille = styled(({ size, noBorder, ...props }: PastilleProps & React.JSX.IntrinsicElements['div']) => {
  return <BasePastille {...props} $size={size} $noBorder={noBorder} />
})``

Pastille.displayName = 'S.image.Pastille'

export const Flag = styled.img<React.ImgHTMLAttributes<HTMLImageElement>>`
  width: 20px;
  height: 15px;
  margin: 0 8px 0 0;
`
Flag.displayName = 'S.image.Flag'
